import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Briefcase, Clock, CheckCircle, XCircle, LogOut, User, FileText, 
  MessageSquare, ChevronRight, Calendar, DollarSign, Star, MapPin,
  Package, TrendingUp, AlertCircle, Mail, Phone, Building, Heart, Trash2,
  ChevronLeft, Settings, Bell
} from 'lucide-react';
import ChatWidget from '../components/ChatWidget';
import { toast } from '../components/ui/sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://revocut-production.up.railway.app';

// Customer Calendar Component
const CustomerCalendarView = ({ orders, formatPrice }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const startingDayOfWeek = firstDayOfMonth.getDay() === 0 ? 6 : firstDayOfMonth.getDay() - 1;
  const daysInMonth = lastDayOfMonth.getDate();
  
  const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 
                      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
  const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  
  const getOrdersForDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return orders.filter(order => {
      const orderDate = order.created_at?.split('T')[0];
      return orderDate === dateStr;
    });
  };
  
  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#F59E0B';
      case 'assigned':
      case 'in_progress': return '#5C63FF';
      case 'completed': return '#10B981';
      case 'cancelled': return '#EF4444';
      default: return '#6B7280';
    }
  };
  
  const getStatusLabel = (status) => {
    switch(status) {
      case 'pending': return 'Ausstehend';
      case 'assigned': return 'Zugewiesen';
      case 'in_progress': return 'In Bearbeitung';
      case 'completed': return 'Abgeschlossen';
      case 'cancelled': return 'Storniert';
      default: return status;
    }
  };
  
  const calendarDays = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }
  
  const selectedOrders = selectedDate ? getOrdersForDate(selectedDate) : [];
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Calendar */}
      <div className="lg:col-span-2">
        <div className="p-6 rounded-2xl border border-white/10" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button 
              onClick={prevMonth}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h3 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {monthNames[month]} {year}
            </h3>
            <button 
              onClick={nextMonth}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          
          {/* Day names */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center text-sm text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, index) => {
              if (!day) return <div key={index} className="h-20" />;
              
              const dayOrders = getOrdersForDate(day);
              const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
              const isSelected = selectedDate === day;
              
              return (
                <button
                  key={index}
                  onClick={() => setSelectedDate(day)}
                  className={`h-20 p-1 rounded-lg border transition-all text-left flex flex-col ${
                    isSelected 
                      ? 'border-purple-500 bg-purple-500/10' 
                      : isToday 
                        ? 'border-purple-500/50 bg-white/5' 
                        : 'border-white/5 hover:border-white/20 hover:bg-white/5'
                  }`}
                >
                  <span className={`text-sm ${isToday ? 'text-purple-400 font-bold' : 'text-gray-400'}`}>
                    {day}
                  </span>
                  <div className="flex flex-wrap gap-1 mt-1 overflow-hidden">
                    {dayOrders.slice(0, 3).map((order, i) => (
                      <div 
                        key={i}
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: getStatusColor(order.status) }}
                        title={order.project_type}
                      />
                    ))}
                    {dayOrders.length > 3 && (
                      <span className="text-xs text-gray-500">+{dayOrders.length - 3}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-6 pt-4 border-t border-white/10">
            {[
              { status: 'pending', label: 'Ausstehend' },
              { status: 'in_progress', label: 'Aktiv' },
              { status: 'completed', label: 'Abgeschlossen' },
              { status: 'cancelled', label: 'Storniert' },
            ].map(item => (
              <div key={item.status} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: getStatusColor(item.status) }}
                />
                <span className="text-xs text-gray-400">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Selected day orders */}
      <div>
        <div className="p-6 rounded-2xl border border-white/10 h-full" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
          <h4 className="font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {selectedDate 
              ? `${selectedDate}. ${monthNames[month]}` 
              : 'Datum auswählen'}
          </h4>
          
          {!selectedDate ? (
            <p className="text-gray-500 text-sm">Klicke auf einen Tag um die Aufträge anzuzeigen.</p>
          ) : selectedOrders.length === 0 ? (
            <p className="text-gray-500 text-sm">Keine Aufträge an diesem Tag.</p>
          ) : (
            <div className="space-y-3">
              {selectedOrders.map((order, i) => (
                <div 
                  key={i}
                  className="p-3 rounded-xl border border-white/10"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{order.project_type || 'Video Projekt'}</p>
                      <p className="text-xs text-gray-500">
                        {order.video_length} Min
                      </p>
                    </div>
                    <span 
                      className="px-2 py-0.5 rounded-full text-xs whitespace-nowrap"
                      style={{ 
                        backgroundColor: `${getStatusColor(order.status)}20`,
                        color: getStatusColor(order.status)
                      }}
                    >
                      {getStatusLabel(order.status)}
                    </span>
                  </div>
                  {order.total_price && (
                    <p className="text-sm font-bold mt-2" style={{ color: '#5C63FF' }}>
                      €{order.total_price.toLocaleString('de-DE')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTabState] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    return hash || 'orders';
  });
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    window.location.hash = tab;
  };
  
  // Data
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  
  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    email: true,
    push: true,
    newJobs: true,
    statusUpdates: true
  });
  
  // Settings menu dropdown
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('customer_token');
    const userData = localStorage.getItem('customer_user');
    
    if (!token || !userData) {
      navigate('/login');
      return;
    }
    
    try {
      setUser(JSON.parse(userData));
      setLoading(false);
    } catch {
      navigate('/login');
    }
  }, [navigate]);

  // Auto-refresh: Poll for new data every 15 seconds
  useEffect(() => {
    if (!user?.id) return;
    const interval = setInterval(() => {
      if (activeTab === 'orders' || activeTab === 'calendar') fetchOrders(false);
      if (activeTab === 'invoices') fetchInvoices(false);
    }, 15000);
    return () => clearInterval(interval);
  }, [user?.id, activeTab]);

  // Fetch orders
  useEffect(() => {
    if (user?.id && (activeTab === 'orders' || activeTab === 'calendar')) {
      fetchOrders();
    }
  }, [user?.id, activeTab]);

  // Fetch invoices
  useEffect(() => {
    if (user?.id && activeTab === 'invoices') {
      fetchInvoices();
    }
  }, [user?.id, activeTab]);

  // Fetch favorites
  useEffect(() => {
    if (user?.id && activeTab === 'favorites') {
      fetchFavorites();
    }
  }, [user?.id, activeTab]);

  // Fetch notification settings
  useEffect(() => {
    if (user?.id && activeTab === 'settings') {
      fetchNotificationSettings();
    }
  }, [user?.id, activeTab]);

  const fetchOrders = async (showLoading = true) => {
    if (showLoading) setLoadingOrders(true);
    try {
      const res = await fetch(`${API_URL}/api/customer/${user.id}/orders`);
      if (res.ok) {
        const data = await res.json();
        setOrders(data);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchInvoices = async (showLoading = true) => {
    if (showLoading) setLoadingInvoices(true);
    try {
      const res = await fetch(`${API_URL}/api/customer/${user.id}/invoices`);
      if (res.ok) {
        const data = await res.json();
        setInvoices(data);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const fetchFavorites = async () => {
    setLoadingFavorites(true);
    try {
      const res = await fetch(`${API_URL}/api/customer/${user.id}/favorites/list`);
      if (res.ok) {
        const data = await res.json();
        setFavorites(data);
      }
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoadingFavorites(false);
    }
  };

  const removeFavorite = async (freelancerId) => {
    try {
      const res = await fetch(`${API_URL}/api/customer/${user.id}/favorites`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ freelancer_id: freelancerId })
      });
      if (res.ok) {
        setFavorites(prev => prev.filter(f => f.id !== freelancerId));
      }
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_user');
    navigate('/login');
  };

  // Fetch notification settings
  const fetchNotificationSettings = async () => {
    if (!user?.id) return;
    try {
      const res = await fetch(`${API_URL}/api/users/${user.id}/notification-settings`);
      if (res.ok) {
        const data = await res.json();
        setNotificationSettings(data);
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    }
  };

  // Save notification settings
  const saveNotificationSettings = async () => {
    if (!user?.id) return;
    try {
      const res = await fetch(`${API_URL}/api/users/${user.id}/notification-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(notificationSettings)
      });
      
      if (res.ok) {
        toast.success('Benachrichtigungseinstellungen gespeichert!');
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving notification settings:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  // Stats
  const stats = {
    totalOrders: orders.length,
    activeOrders: orders.filter(o => ['pending', 'assigned', 'in_progress'].includes(o.status)).length,
    completedOrders: orders.filter(o => o.status === 'completed').length,
    totalSpent: orders.reduce((sum, o) => sum + (o.total_price || 0), 0)
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Ausstehend', color: 'text-yellow-400 bg-yellow-400/10' },
      assigned: { label: 'Zugewiesen', color: 'text-blue-400 bg-blue-400/10' },
      in_progress: { label: 'In Bearbeitung', color: 'text-purple-400 bg-purple-400/10' },
      completed: { label: 'Abgeschlossen', color: 'text-green-400 bg-green-400/10' },
      cancelled: { label: 'Storniert', color: 'text-red-400 bg-red-400/10' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getInvoiceStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Entwurf', color: 'text-gray-400 bg-gray-400/10' },
      sent: { label: 'Offen', color: 'text-yellow-400 bg-yellow-400/10' },
      paid: { label: 'Bezahlt', color: 'text-green-400 bg-green-400/10' },
      overdue: { label: 'Überfällig', color: 'text-red-400 bg-red-400/10' }
    };
    const config = statusConfig[status] || statusConfig.sent;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Header */}
      <header className="px-6 lg:px-12 py-4 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/revocut-logo.png" alt="RevoCut" className="w-10 h-10" />
            <span className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>REVOCUT</span>
          </Link>
          
          <div className="flex items-center gap-3">
            {/* Back to Website Button */}
            <Link 
              to="/"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white text-sm"
            >
              Zur Webseite
            </Link>
            
            {/* User Info */}
            <div className="flex items-center gap-2 text-sm">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                style={{ backgroundColor: '#5C63FF' }}
              >
                {user?.name?.charAt(0).toUpperCase() || 'K'}
              </div>
              <span className="hidden sm:inline">{user?.name}</span>
            </div>
            
            {/* Settings Dropdown */}
            <div className="relative">
              <button 
                onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
                title="Einstellungen"
              >
                <Settings className="w-5 h-5" />
              </button>
              
              {showSettingsMenu && (
                <div 
                  className="absolute right-0 top-full mt-2 w-48 rounded-xl border border-white/10 shadow-xl overflow-hidden"
                  style={{ backgroundColor: '#1a1a1a', zIndex: 9999 }}
                >
                  <button
                    onClick={() => {
                      setShowSettingsMenu(false);
                      setActiveTab('settings');
                    }}
                    className="w-full px-4 py-3 text-left text-sm hover:bg-white/5 flex items-center gap-2"
                  >
                    <Bell className="w-4 h-4 text-purple-400" />
                    Benachrichtigungen
                  </button>
                  <button
                    onClick={() => {
                      setShowSettingsMenu(false);
                      handleLogout();
                    }}
                    className="w-full px-4 py-3 text-left text-sm hover:bg-white/5 flex items-center gap-2 text-red-400"
                  >
                    <LogOut className="w-4 h-4" />
                    Abmelden
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="px-6 lg:px-12 py-8 max-w-7xl mx-auto">
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Hallo, {user?.name?.split(' ')[0] || 'Kunde'}! 👋
            </h1>
            <p className="text-gray-400">Verwalte deine Projekte und Rechnungen</p>
          </div>
          
          <Link
            to="/#map"
            className="mt-4 md:mt-0 px-6 py-3 rounded-xl font-medium transition-all hover:opacity-90 flex items-center gap-2"
            style={{ backgroundColor: '#5C63FF' }}
          >
            <Briefcase className="w-5 h-5" />
            Neues Projekt starten
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Alle Aufträge', value: stats.totalOrders, icon: Package, color: '#5C63FF' },
            { label: 'Aktive Aufträge', value: stats.activeOrders, icon: Clock, color: '#F59E0B' },
            { label: 'Abgeschlossen', value: stats.completedOrders, icon: CheckCircle, color: '#10B981' },
            { label: 'Ausgegeben', value: `€${stats.totalSpent.toLocaleString('de-DE')}`, icon: DollarSign, color: '#8B5CF6' }
          ].map((stat, idx) => (
            <div 
              key={idx}
              className="p-5 rounded-2xl border border-white/10"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${stat.color}20` }}
                >
                  <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                </div>
              </div>
              <p className="text-2xl font-bold mb-1">{stat.value}</p>
              <p className="text-sm text-gray-400">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto">
          {[
            { id: 'orders', label: 'Meine Aufträge', icon: Briefcase, count: orders.length },
            { id: 'calendar', label: 'Kalender', icon: Calendar, count: null },
            { id: 'favorites', label: 'Favoriten', icon: Heart, count: favorites.length },
            { id: 'invoices', label: 'Rechnungen', icon: FileText, count: invoices.length },
            { id: 'profile', label: 'Mein Profil', icon: User, count: null },
            { id: 'settings', label: 'Einstellungen', icon: Settings, count: null }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-purple-500/20 text-purple-400'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-4">
            {loadingOrders ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-20">
                <Package className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-xl font-bold mb-2">Keine Aufträge</h3>
                <p className="text-gray-400 mb-6">
                  Du hast noch keine Projekte gestartet.
                </p>
                <Link
                  to="/#map"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all"
                  style={{ backgroundColor: '#5C63FF' }}
                >
                  Freelancer finden
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              orders.map((order) => (
                <div 
                  key={order.id}
                  className="p-6 rounded-2xl border border-white/10 hover:border-purple-500/30 transition-all"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-bold text-lg">{order.project_type || 'Video Projekt'}</h3>
                        {getStatusBadge(order.status)}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(order.created_at).toLocaleDateString('de-DE')}
                        </div>
                        {order.video_length && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {order.video_length} Min
                          </div>
                        )}
                        {order.total_price && (
                          <div className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            €{order.total_price.toLocaleString('de-DE')}
                          </div>
                        )}
                      </div>

                      {order.description && (
                        <p className="text-gray-400 mt-3 line-clamp-2">{order.description}</p>
                      )}
                    </div>

                    {/* Freelancer Info */}
                    {order.freelancer && (
                      <div 
                        className="flex items-center gap-4 p-4 rounded-xl"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }}
                      >
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: 'rgba(92, 99, 255, 0.15)' }}
                        >
                          <span className="font-bold" style={{ color: '#5C63FF' }}>
                            {order.freelancer.name?.charAt(0) || 'F'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{order.freelancer.name}</p>
                          <p className="text-sm text-gray-400">{order.freelancer.specialization || 'Video Editor'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Favorites Tab */}
        {activeTab === 'favorites' && (
          <div className="space-y-4">
            {loadingFavorites ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : favorites.length === 0 ? (
              <div className="text-center py-20">
                <Heart className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-xl font-bold mb-2">Keine Favoriten</h3>
                <p className="text-gray-400 mb-6">
                  Du hast noch keine Freelancer zu deinen Favoriten hinzugefügt.
                </p>
                <Link
                  to="/#map"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all"
                  style={{ backgroundColor: '#5C63FF' }}
                >
                  Freelancer entdecken
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {favorites.map((freelancer) => (
                  <div 
                    key={freelancer.id}
                    className="p-5 rounded-2xl border border-white/10 hover:border-purple-500/30 transition-all"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: 'rgba(92, 99, 255, 0.15)' }}
                        >
                          <span className="font-bold text-lg" style={{ color: '#5C63FF' }}>
                            {freelancer.name?.charAt(0) || 'F'}
                          </span>
                        </div>
                        <div>
                          <h4 className="font-semibold">{freelancer.name}</h4>
                          <p className="text-sm text-gray-400">{freelancer.specialization || 'Video Editor'}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFavorite(freelancer.id)}
                        className="p-2 rounded-lg hover:bg-red-500/10 transition-colors"
                        title="Aus Favoriten entfernen"
                      >
                        <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-400" />
                      </button>
                    </div>
                    
                    <div className="flex items-center gap-4 mb-4 text-sm">
                      <div className="flex items-center gap-1 text-yellow-500">
                        <Star className="w-4 h-4 fill-current" />
                        <span>{(freelancer.rating || 5).toFixed(1)}</span>
                        {freelancer.review_count > 0 && (
                          <span className="text-gray-500">({freelancer.review_count})</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-gray-400">
                        <MapPin className="w-4 h-4" />
                        <span>{freelancer.location || 'Deutschland'}</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/freelancer/${freelancer.id}/profile`)}
                        className="flex-1 py-2 rounded-xl text-sm font-medium transition-all hover:bg-white/10"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
                      >
                        Profil
                      </button>
                      <button
                        onClick={() => navigate(`/calculator/${freelancer.id}`)}
                        className="flex-1 py-2 rounded-xl text-sm font-medium transition-all hover:opacity-90"
                        style={{ backgroundColor: '#5C63FF' }}
                      >
                        Buchen
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div className="space-y-4">
            {loadingInvoices ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-20">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-xl font-bold mb-2">Keine Rechnungen</h3>
                <p className="text-gray-400">
                  Du hast noch keine Rechnungen erhalten.
                </p>
              </div>
            ) : (
              <div className="rounded-2xl border border-white/10 overflow-hidden" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-4 text-gray-400 font-medium">Rechnung</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Datum</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Betrag</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Status</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Fällig</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-4">
                          <span className="font-medium">#{invoice.invoice_number || invoice.id?.slice(-6)}</span>
                        </td>
                        <td className="p-4 text-gray-400">
                          {new Date(invoice.created_at).toLocaleDateString('de-DE')}
                        </td>
                        <td className="p-4 font-medium">
                          €{(invoice.amount || 0).toLocaleString('de-DE')}
                        </td>
                        <td className="p-4">
                          {getInvoiceStatusBadge(invoice.status)}
                        </td>
                        <td className="p-4 text-gray-400">
                          {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('de-DE') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="max-w-2xl">
            <div 
              className="rounded-2xl border border-white/10 p-8"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
            >
              <div className="flex items-center gap-6 mb-8">
                <div 
                  className="w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-bold"
                  style={{ backgroundColor: '#5C63FF' }}
                >
                  {user?.name?.charAt(0) || 'K'}
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{user?.name}</h2>
                  <p className="text-gray-400">Kunde seit {new Date().getFullYear()}</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">E-Mail</label>
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <span>{user?.email}</span>
                  </div>
                </div>

                {user?.phone && (
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Telefon</label>
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5">
                      <Phone className="w-5 h-5 text-gray-400" />
                      <span>{user.phone}</span>
                    </div>
                  </div>
                )}

                {user?.company && (
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Firma</label>
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5">
                      <Building className="w-5 h-5 text-gray-400" />
                      <span>{user.company}</span>
                    </div>
                  </div>
                )}

                <div className="pt-6 border-t border-white/10">
                  <button
                    onClick={handleLogout}
                    className="w-full py-3 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors flex items-center justify-center gap-2"
                  >
                    <LogOut className="w-5 h-5" />
                    Abmelden
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <CustomerCalendarView orders={orders} formatPrice={(p) => `€${(p || 0).toLocaleString('de-DE')}`} />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="max-w-2xl">
            <div 
              className="rounded-2xl border border-white/10 p-6"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
            >
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <Bell className="w-5 h-5 text-yellow-400" />
                Benachrichtigungen
              </h3>
              
              <div className="space-y-4">
                <label className="flex items-center justify-between cursor-pointer p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="font-medium">E-Mail Benachrichtigungen</p>
                      <p className="text-xs text-gray-500">Updates per E-Mail erhalten</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={notificationSettings.email}
                    onChange={(e) => setNotificationSettings({...notificationSettings, email: e.target.checked})}
                    className="w-5 h-5 rounded accent-purple-500"
                  />
                </label>
                
                <label className="flex items-center justify-between cursor-pointer p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                  <div className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-yellow-400" />
                    <div>
                      <p className="font-medium">Push-Benachrichtigungen</p>
                      <p className="text-xs text-gray-500">Popups im Browser</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={notificationSettings.push}
                    onChange={(e) => setNotificationSettings({...notificationSettings, push: e.target.checked})}
                    className="w-5 h-5 rounded accent-purple-500"
                  />
                </label>
                
                <label className="flex items-center justify-between cursor-pointer p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-5 h-5 text-green-400" />
                    <div>
                      <p className="font-medium">Auftrags-Updates</p>
                      <p className="text-xs text-gray-500">Bei neuen Statusänderungen</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={notificationSettings.newJobs}
                    onChange={(e) => setNotificationSettings({...notificationSettings, newJobs: e.target.checked})}
                    className="w-5 h-5 rounded accent-purple-500"
                  />
                </label>
                
                <label className="flex items-center justify-between cursor-pointer p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-purple-400" />
                    <div>
                      <p className="font-medium">Status-Updates</p>
                      <p className="text-xs text-gray-500">Bei Änderungen informiert werden</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={notificationSettings.statusUpdates}
                    onChange={(e) => setNotificationSettings({...notificationSettings, statusUpdates: e.target.checked})}
                    className="w-5 h-5 rounded accent-purple-500"
                  />
                </label>
                
                <button
                  onClick={saveNotificationSettings}
                  className="w-full py-3 rounded-xl font-medium transition-all bg-purple-500 hover:bg-purple-600 flex items-center justify-center gap-2"
                >
                  Einstellungen speichern
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Chat Widget */}
      <ChatWidget 
        userId={user?.id} 
        userName={user?.name} 
        userType="customer" 
      />
    </div>
  );
};

export default CustomerDashboard;
